// pages/strategic-playbook/decisions/index.js
// ════════════════════════════════════════════════════
// DECISION LOG — list view (v1)
// ════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import {
  listDecisions,
  getStats,
  CONVICTION_TIERS,
  STATUSES,
  seedDemoIfEmpty,
} from '../../../lib/strategic-playbook/decisions';

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];

export default function DecisionLog() {
  const [decisions, setDecisions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    seedDemoIfEmpty();
    setDecisions(listDecisions());
    setLoaded(true);
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return decisions;
    if (filter === 'active') return decisions.filter((d) => d.status === 'active');
    if (filter === 'resolved')
      return decisions.filter((d) => ['won', 'lost', 'mixed'].includes(d.status));
    return decisions.filter((d) => d.status === filter);
  }, [decisions, filter]);

  const stats = useMemo(() => getStats(decisions), [decisions]);

  return (
    <div className="sp-root">
      <Head>
        <title>Decision Log · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <DecisionsTopbar />

        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
              Personal Decision Log
            </div>
            <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
              Your Decisions
            </h1>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 640, margin: 0 }}>
              Capture decisions at the moment with conviction tier, thesis, fears, and exit
              criteria. Report outcomes at 30/60/90/180 days. Over time becomes the only
              honest record of your decision-making — and the data the Conviction Calibrator
              needs.
            </p>
          </div>
          <Link
            href="/strategic-playbook/decisions/new"
            style={{
              padding: '12px 22px',
              background: 'var(--sp-brass-bg)',
              border: '1px solid var(--sp-aged-brass)',
              borderRadius: 'var(--sp-radius-card)',
              color: 'var(--sp-aged-brass)',
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.16em',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            + Log Decision
          </Link>
        </div>

        {loaded && decisions.length > 0 && (
          <>
            {/* Stats strip */}
            <StatsStrip stats={stats} />

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, marginTop: 24, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(232, 221, 196, 0.45)', marginRight: 4 }}>
                Filter
              </span>
              {FILTER_OPTIONS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  style={{
                    fontFamily: 'var(--sp-font-mono)',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.10em',
                    padding: '6px 14px',
                    background: filter === f.id ? 'var(--sp-brass-bg)' : 'transparent',
                    color: filter === f.id ? 'var(--sp-aged-brass)' : 'rgba(232, 221, 196, 0.55)',
                    border: `1px solid ${filter === f.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                    borderRadius: 'var(--sp-radius)',
                    cursor: 'pointer',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* List */}
            {filtered.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'rgba(232, 221, 196, 0.50)', fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 15 }}>
                No decisions match this filter.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {filtered.map((d) => (
                  <DecisionCard key={d.id} decision={d} />
                ))}
              </div>
            )}
          </>
        )}

        {loaded && decisions.length === 0 && <EmptyState />}
      </div>
    </div>
  );
}

function StatsStrip({ stats }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}
    >
      <StatCard label="Total decisions" value={stats.total} />
      <StatCard label="Active" value={stats.byStatus.active || 0} color="var(--sp-aged-brass)" />
      <StatCard
        label="Resolved"
        value={stats.resolvedCount}
        color="rgba(232, 221, 196, 0.65)"
      />
      <StatCard
        label="Win rate"
        value={
          stats.overallWinRate !== null
            ? `${stats.overallWinRate.toFixed(0)}%`
            : '—'
        }
        color={stats.overallWinRate !== null && stats.overallWinRate >= 50 ? 'var(--sp-forest-moss)' : 'var(--sp-cognac)'}
      />
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ padding: '16px 18px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)' }}>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 26, color: color || 'var(--sp-warm-cream)' }}>
        {value}
      </div>
    </div>
  );
}

function DecisionCard({ decision }) {
  const conviction = CONVICTION_TIERS.find((c) => c.id === decision.convictionTier);
  const status = STATUSES.find((s) => s.id === decision.status);
  const ageDays = Math.floor((Date.now() - new Date(decision.createdAt).getTime()) / 86400000);

  return (
    <Link
      href={`/strategic-playbook/decisions/${decision.id}`}
      className="sp-dossier-card"
      style={{ padding: 0, overflow: 'hidden' }}
    >
      <div
        style={{
          height: 3,
          background: status?.color || 'var(--sp-aged-brass)',
          opacity: 0.7,
        }}
      />
      <div style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, padding: '3px 8px', border: `1px solid ${conviction?.color || 'var(--sp-aged-brass)'}`, borderRadius: 'var(--sp-radius)', color: conviction?.color || 'var(--sp-aged-brass)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {conviction?.label || 'Unknown'}
            </span>
            <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, padding: '3px 8px', border: `1px solid ${status?.color || 'rgba(232, 221, 196, 0.30)'}`, borderRadius: 'var(--sp-radius)', color: status?.color || 'var(--sp-aged-brass)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {status?.label || decision.status}
            </span>
            {decision.isDemo && (
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, color: 'rgba(232, 221, 196, 0.40)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                · demo
              </span>
            )}
          </div>
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'rgba(232, 221, 196, 0.40)' }}>
            {ageDays === 0 ? 'today' : `${ageDays}d ago`}
          </span>
        </div>
        <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 15, lineHeight: 1.5, color: 'var(--sp-warm-cream)', marginBottom: 10 }}>
          {decision.decision.length > 180 ? decision.decision.slice(0, 180) + '...' : decision.decision}
        </div>
        {(decision.pillars || []).length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {decision.pillars.slice(0, 4).map((p) => (
              <span key={p} className={`sp-tag sp-pillar-bg-${p} sp-pillar-${p}`}>
                P{p}
              </span>
            ))}
            {decision.outcomes && decision.outcomes.length > 0 && (
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, padding: '3px 8px', color: 'var(--sp-aged-brass)', letterSpacing: '0.08em', marginLeft: 4 }}>
                · {decision.outcomes.length} outcome{decision.outcomes.length > 1 ? 's' : ''} logged
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        padding: 64,
        textAlign: 'center',
        background: 'var(--sp-soft-charcoal)',
        border: '1px dashed var(--sp-divider)',
        borderRadius: 'var(--sp-radius-card)',
      }}
    >
      <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 22, color: 'var(--sp-warm-cream)', marginBottom: 12 }}>
        No decisions logged yet.
      </div>
      <div style={{ fontSize: 14, color: 'rgba(232, 221, 196, 0.65)', marginBottom: 24, maxWidth: 480, margin: '0 auto 24px', lineHeight: 1.6 }}>
        Capture your first decision while you&apos;re making it — not after. Tag conviction
        tier, write the thesis + fears + exit criteria. Outcomes get logged at 30/60/90/180
        days. Over time becomes the only honest record of your decision-making.
      </div>
      <Link
        href="/strategic-playbook/decisions/new"
        style={{
          display: 'inline-block',
          padding: '12px 24px',
          background: 'var(--sp-brass-bg)',
          border: '1px solid var(--sp-aged-brass)',
          borderRadius: 'var(--sp-radius-card)',
          color: 'var(--sp-aged-brass)',
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.16em',
          textDecoration: 'none',
        }}
      >
        Log First Decision →
      </Link>
    </div>
  );
}

function DecisionsTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Decision Log · personal layer · stored locally</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools">Tools</Link>
        <Link href="/strategic-playbook/decisions" className="sp-active">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
